import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ref, onValue, off } from "firebase/database";
import { db } from "../firebase/config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./PriceHistoryScreen.module.css";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PropertyFormDataDisplay from "./PropertyFormDataDisplay";
import { PROPERTY_TYPE_LABELS } from "../constants/aiPriceData";
import { useCheckProcessingTasks } from "../hooks/useCheckProcessingTasks";
import {
  FiClock,
  FiEye,
  FiDownload,
  FiRefreshCw,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiFileText,
  FiArchive,
  FiActivity,
  FiUsers,
  FiLoader,
  FiPlay,
  FiHome,
  FiMapPin,
} from "react-icons/fi";

const PriceHistoryScreen = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const location = useLocation();
  const [histories, setHistories] = useState([]);
  const [queueRequests, setQueueRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queueLoading, setQueueLoading] = useState(true);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFullReportModal, setShowFullReportModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const pageRef = useRef(null);

  const userId = localStorage.getItem("userId");

  // Hook để check processing tasks một lần khi load
  const { checkProcessingTasks, resetCheck } = useCheckProcessingTasks(userId);
  const hasCheckedRef = useRef(false);

  // Helper function: Xác định loại định giá (bán hay thuê)
  const getPriceType = useCallback((item) => {
    const title = item.current_task_title || "";
    if (title.toLowerCase().includes("thuê")) {
      return {
        label: "Thuê BDS",
        bgColor: "bg-purple-100",
        textColor: "text-purple-700",
        borderColor: "border-purple-300",
        icon: "🏠",
      };
    }
    return {
      label: "Bán BDS",
      bgColor: "bg-blue-100",
      textColor: "text-blue-700",
      borderColor: "border-blue-300",
      icon: "💰",
    };
  }, []);

  // Helper function: Trích xuất thông tin đặc trưng từ form_data
  const getPropertySummary = useCallback((item) => {
    // Nếu có form_data, sử dụng nó để tạo summary
    if (item.form_data && item.form_data.propertyType) {
      const formData = item.form_data;
      const propertyTypeLabel =
        PROPERTY_TYPE_LABELS[formData.propertyType] || "BĐS";

      // Lấy loại cụ thể
      let specificType = "";
      if (formData.apartmentType) specificType = formData.apartmentType;
      else if (formData.houseType) specificType = formData.houseType;
      else if (formData.landType) specificType = formData.landType;
      else if (formData.propertyTypeOther)
        specificType = formData.propertyTypeOther;

      // Lấy địa chỉ
      let locationInfo = "";
      if (formData.projectName) {
        locationInfo = formData.projectName;
      } else if (formData.address) {
        // Rút gọn địa chỉ nếu quá dài
        const address = formData.address;
        locationInfo =
          address.length > 50 ? address.substring(0, 50) + "..." : address;
      }

      // Lấy diện tích
      let areaInfo = "";
      if (formData.area) {
        areaInfo = `${formData.area}m²`;
      } else if (formData.landArea) {
        areaInfo = `${formData.landArea}m²`;
      }

      // Lấy số phòng ngủ
      let bedroomInfo = "";
      if (formData.bedrooms) {
        bedroomInfo = `${formData.bedrooms} PN`;
      }

      // Ghép thông tin
      const parts = [propertyTypeLabel];
      if (specificType) parts.push(specificType);
      if (areaInfo) parts.push(areaInfo);
      if (bedroomInfo) parts.push(bedroomInfo);

      return {
        title: parts.join(" • "),
        location: locationInfo,
        hasFormData: true,
      };
    }

    // Fallback: Sử dụng message hoặc title
    return {
      title: item.current_task_title || "Yêu cầu định giá BĐS",
      location: "",
      hasFormData: false,
    };
  }, []);

  // Ref để lưu trữ dữ liệu queue, processing và histories
  const queueRequestsRef = useRef({
    queueData: null,
    processingData: null,
    historiesData: null,
  });

  // Function to combine and update queue requests (tránh trùng lặp với histories)
  const updateQueueRequests = useCallback(
    (queueData, processingData, historiesData) => {
      // Use current state if parameter is null
      if (queueData === null) {
        queueData = queueRequestsRef.current.queueData;
      } else {
        queueRequestsRef.current.queueData = queueData;
      }

      if (processingData === null) {
        processingData = queueRequestsRef.current.processingData;
      } else {
        queueRequestsRef.current.processingData = processingData;
      }

      if (historiesData === null) {
        historiesData = queueRequestsRef.current.historiesData;
      } else {
        queueRequestsRef.current.historiesData = historiesData;
      }

      const allRequests = [];

      // Tạo set các request_id đã có trong histories để tránh trùng lặp
      const existingHistoryRequestIds = new Set();
      if (historiesData) {
        Object.values(historiesData).forEach((historyItem) => {
          if (historyItem.request_id) {
            existingHistoryRequestIds.add(historyItem.request_id);
          }
        });
      }

      // Add pending requests from queue_message (luôn hiển thị vì chưa có trong histories)
      if (queueData) {
        Object.entries(queueData).forEach(([requestId, requestData]) => {
          if (
            requestData?.user_id === userId &&
            requestData?.queue_type === "pending"
          ) {
            allRequests.push({
              request_id: requestId,
              ...requestData,
              queue_type: "pending",
            });
          }
        });
      }

      // Add processing requests from queue_processing CHỈ KHI CHƯA CÓ TRONG HISTORIES
      if (processingData) {
        Object.entries(processingData).forEach(([requestId, requestData]) => {
          if (
            requestData?.user_id === userId &&
            !existingHistoryRequestIds.has(requestId) // Chỉ thêm nếu chưa có trong histories
          ) {
            allRequests.push({
              request_id: requestId,
              ...requestData,
              queue_type: "processing",
            });
          }
        });
      }

      // Sort: processing first, then pending by creation time
      const sortedRequests = allRequests.sort((a, b) => {
        if (a.queue_type === "processing" && b.queue_type === "pending")
          return -1;
        if (a.queue_type === "pending" && b.queue_type === "processing")
          return 1;

        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateA - dateB; // Oldest first
      });

      setQueueRequests(sortedRequests);
      setQueueLoading(false);
    },
    [userId]
  );

  useEffect(() => {
    if (!userId) {
      navigate("/");
      return;
    }

    // Listen to histories
    const historiesRef = ref(db, `histories/${userId}`);
    const historiesUnsubscribe = onValue(
      historiesRef,
      (snapshot) => {
        const data = snapshot.val();

        if (!data) {
          setHistories([]);
          setLoading(false);
          // Cập nhật queue requests khi histories thay đổi
          updateQueueRequests(null, null, {});
          return;
        }

        const historyArray = Object.entries(data)
          .map(([taskId, historyData]) => ({
            taskId,
            ...historyData,
          }))
          .sort((a, b) => {
            const dateA = new Date(a.created_at || a.updated_at || 0);
            const dateB = new Date(b.created_at || b.updated_at || 0);
            return dateB - dateA;
          });

        setHistories(historyArray);
        setLoading(false);

        // ✅ CHECK PROCESSING TASKS MỘT LẦN KHI LOAD HISTORIES LẦN ĐẦU
        // Chỉ check một lần khi histories được load lần đầu tiên
        if (!hasCheckedRef.current && historyArray.length > 0) {
          hasCheckedRef.current = true;
          // Delay một chút để đảm bảo UI đã render xong
          setTimeout(() => {
            checkProcessingTasks(historyArray);
          }, 500);
        }

        // Cập nhật queue requests khi histories thay đổi để tránh trùng lặp
        updateQueueRequests(null, null, data);
      },
      (error) => {
        console.error("Lỗi khi lấy lịch sử:", error);
        toast.error("Không thể lấy dữ liệu lịch sử!");
        setLoading(false);
      }
    );

    // Listen to queue_message for pending requests
    const queueRef = ref(db, "queue_message");
    const queueUnsubscribe = onValue(
      queueRef,
      (snapshot) => {
        const queueData = snapshot.val();
        updateQueueRequests(queueData, null, null);
      },
      (error) => {
        console.error("Lỗi khi lấy queue requests:", error);
        setQueueRequests([]);
        setQueueLoading(false);
      }
    );

    // Listen to queue_processing for processing requests
    const processingRef = ref(db, "queue_processing");
    const processingUnsubscribe = onValue(
      processingRef,
      (snapshot) => {
        const processingData = snapshot.val();
        updateQueueRequests(null, processingData, null);
      },
      (error) => {
        console.error("Lỗi khi lấy processing requests:", error);
      }
    );

    return () => {
      off(historiesRef, "value", historiesUnsubscribe);
      off(queueRef, "value", queueUnsubscribe);
      off(processingRef, "value", processingUnsubscribe);
    };
  }, [userId, navigate, updateQueueRequests, checkProcessingTasks]);

  // Reset check flag khi userId thay đổi
  useEffect(() => {
    hasCheckedRef.current = false;
    resetCheck();
  }, [userId, resetCheck]);

  // Scroll to top khi component mount hoặc khi có request mới
  useEffect(() => {
    // Scroll window lên đầu
    window.scrollTo(0, 0);
  }, []);

  // Scroll lên đầu khi có request mới (pending requests)
  useEffect(() => {
    if (queueRequests.length > 0) {
      window.scrollTo(0, 0);
    }
  }, [queueRequests]);

  // Scroll lên đầu khi loading xong
  useEffect(() => {
    if (!loading && !queueLoading) {
      window.scrollTo(0, 0);
    }
  }, [loading, queueLoading]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Không có thông tin";

    try {
      const date = new Date(dateString);
      return date.toLocaleString("vi-VN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Thời gian không hợp lệ";
    }
  }, []);

  const getStatusInfo = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return {
          icon: <FiClock />,
          color: "text-yellow-500",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-200",
          text: "Chờ xử lý",
        };
      case "processing":
        return {
          icon: <FiRefreshCw className="animate-spin" />,
          color: "text-orange-500",
          bgColor: "bg-orange-50",
          borderColor: "border-orange-200",
          text: "Đang xử lý",
        };
      case "crawling":
        return {
          icon: <FiRefreshCw className="animate-spin" />,
          color: "text-indigo-500",
          bgColor: "bg-indigo-50",
          borderColor: "border-indigo-200",
          text: "Đang crawl",
        };
      case "doing":
      case "in_progress":
      case "not_completed":
        return {
          icon: <FiRefreshCw className="animate-spin" />,
          color: "text-blue-500",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
          text: "Đang xử lý",
        };
      case "uploading":
        return {
          icon: <FiRefreshCw className="animate-spin" />,
          color: "text-purple-500",
          bgColor: "bg-purple-50",
          borderColor: "border-purple-200",
          text: "Đang tải lên",
        };
      case "completed":
      case "success":
        return {
          icon: <FiCheckCircle />,
          color: "text-green-500",
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          text: "Hoàn thành",
        };
      case "failed":
      case "error":
        return {
          icon: <FiXCircle />,
          color: "text-red-500",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          text: "Thất bại",
        };
      default:
        return {
          icon: <FiAlertCircle />,
          color: "text-gray-500",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
          text: status || "Không rõ",
        };
    }
  }, []);

  // Merge all requests (histories + queue) into single list
  const allRequests = React.useMemo(() => {
    const merged = [];

    // Add all histories
    histories.forEach((history) => {
      merged.push({
        ...history,
        id: history.taskId,
        type: "history",
        created_at: history.created_at || history.updated_at,
      });
    });

    // Add queue requests (that don't exist in histories)
    queueRequests.forEach((request) => {
      merged.push({
        ...request,
        id: request.request_id,
        type: "queue",
        status: request.queue_type === "processing" ? "processing" : "pending",
      });
    });

    // Sort: processing first, then by creation time (newest first)
    return merged.sort((a, b) => {
      // Processing items first
      const statusA = a.status?.toLowerCase() || "";
      const statusB = b.status?.toLowerCase() || "";

      const isProcessingA = [
        "processing",
        "crawling",
        "doing",
        "in_progress",
        "not_completed",
        "uploading",
      ].includes(statusA);
      const isProcessingB = [
        "processing",
        "crawling",
        "doing",
        "in_progress",
        "not_completed",
        "uploading",
      ].includes(statusB);

      if (isProcessingA && !isProcessingB) return -1;
      if (!isProcessingA && isProcessingB) return 1;

      // Then by date (newest first)
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
  }, [histories, queueRequests]);

  const filteredRequests = allRequests.filter((item) => {
    const matchesSearch =
      !searchTerm ||
      (item.message &&
        item.message.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.task_url &&
        item.task_url.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.taskId &&
        item.taskId.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.request_id &&
        item.request_id.toLowerCase().includes(searchTerm.toLowerCase()));

    let matchesStatus = statusFilter === "all";
    if (!matchesStatus && item.status) {
      const currentStatus = item.status.toLowerCase();

      // Nhóm các trạng thái tương đương
      switch (statusFilter.toLowerCase()) {
        case "pending":
          matchesStatus = currentStatus === "pending";
          break;
        case "processing":
          matchesStatus = [
            "doing",
            "processing",
            "in_progress",
            "not_completed",
            "crawling",
            "uploading",
          ].includes(currentStatus);
          break;
        case "completed":
          matchesStatus = ["completed", "success"].includes(currentStatus);
          break;
        case "failed":
          matchesStatus = ["failed", "error"].includes(currentStatus);
          break;
        default:
          matchesStatus = currentStatus === statusFilter.toLowerCase();
      }
    }

    let matchesDate = true;
    if (dateFilter !== "all" && item.created_at) {
      const historyDate = new Date(item.created_at);
      const now = new Date();

      switch (dateFilter) {
        case "today":
          matchesDate = historyDate.toDateString() === now.toDateString();
          break;
        case "week":
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          matchesDate = historyDate >= weekAgo;
          break;
        case "month":
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          matchesDate = historyDate >= monthAgo;
          break;
        default:
          matchesDate = true;
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const openDetailModal = useCallback((history) => {
    setSelectedHistory(history);
    setShowDetailModal(true);
  }, []);

  const closeDetailModal = useCallback(() => {
    setSelectedHistory(null);
    setShowDetailModal(false);
  }, []);

  const openFullReportModal = useCallback(() => {
    setShowFullReportModal(true);
  }, []);

  const closeFullReportModal = useCallback(() => {
    setShowFullReportModal(false);
  }, []);

  // Hàm lấy tất cả files từ S3 (không trùng lặp)
  const getAllS3Files = useCallback((history) => {
    if (!history.crawl_response?.data) return [];

    const allFiles = [];
    const seenUrls = new Set();

    // Lấy files từ downloaded_files
    const downloadedFiles = history.crawl_response.data.downloaded_files || [];
    downloadedFiles.forEach((file) => {
      if (file.s3_url && !seenUrls.has(file.s3_url)) {
        allFiles.push(file);
        seenUrls.add(file.s3_url);
      }
    });

    // Lấy files từ cloudinary_files trong chat messages
    const chatMessages = history.crawl_response.data.chat_messages || [];
    chatMessages.forEach((msg) => {
      if (msg.cloudinary_files) {
        msg.cloudinary_files.forEach((file) => {
          if (file.s3_url && !seenUrls.has(file.s3_url)) {
            allFiles.push(file);
            seenUrls.add(file.s3_url);
          }
        });
      }
    });

    return allFiles;
  }, []);

  // Hàm tính số files
  const getFileCount = useCallback(
    (history) => {
      return getAllS3Files(history).length;
    },
    [getAllS3Files]
  );

  const downloadFile = useCallback((url, filename) => {
    if (!url) {
      toast.error("Không có URL file để tải!");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "file";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Đang tải file: ${filename}`);
    } catch (error) {
      console.error("Lỗi khi tải file:", error);
      toast.error("Có lỗi khi tải file!");
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiRefreshCw className="animate-spin text-4xl text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Đang tải lịch sử...</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={pageRef} className="min-h-screen bg-gray-50 p-2 sm:p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            Lịch Sử Định Giá
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Xem lại tất cả các yêu cầu định giá bất động sản
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4">
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tìm kiếm
              </label>
              <input
                type="text"
                placeholder="Tìm theo nội dung, URL..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trạng thái
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="all">Tất cả</option>
                <option value="pending">Chờ xử lý</option>
                <option value="processing">Đang xử lý</option>
                <option value="completed">Hoàn thành</option>
                <option value="failed">Thất bại</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Thời gian
              </label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="all">Tất cả</option>
                <option value="today">Hôm nay</option>
                <option value="week">7 ngày qua</option>
                <option value="month">30 ngày qua</option>
              </select>
            </div>

            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                <div className="flex items-center">
                  <FiActivity className="mr-1" />
                  <span className="hidden sm:inline">Tổng cộng: </span>
                  {filteredRequests.length} yêu cầu
                </div>
                {queueRequests.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    Đang chờ:{" "}
                    {
                      queueRequests.filter((r) => r.queue_type === "pending")
                        .length
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {loading || queueLoading ? (
            <div className="bg-white rounded-lg p-6 sm:p-8 text-center">
              <FiRefreshCw className="animate-spin text-3xl sm:text-4xl text-blue-500 mx-auto mb-4" />
              <p className="text-sm sm:text-base text-gray-600">
                Đang tải dữ liệu...
              </p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="bg-white rounded-lg p-6 sm:p-8 text-center">
              <FiFileText className="text-3xl sm:text-4xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-600 mb-2">
                Chưa có yêu cầu định giá
              </h3>
              <p className="text-sm sm:text-base text-gray-500 mb-4">
                Bạn chưa có yêu cầu định giá nào. Hãy bắt đầu tạo yêu cầu đầu
                tiên!
              </p>
              <button
                onClick={() => navigate("/ai-price")}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base"
              >
                Định giá ngay
              </button>
            </div>
          ) : (
            filteredRequests.map((item) => {
              const statusInfo = getStatusInfo(item.status);

              // Render queue request item
              if (item.type === "queue") {
                const pendingRequests = queueRequests.filter(
                  (r) => r.queue_type === "pending"
                );
                const position =
                  pendingRequests.findIndex(
                    (r) => r.request_id === item.request_id
                  ) + 1;

                const propertySummary = getPropertySummary(item);

                return (
                  <div
                    key={item.id}
                    className={`bg-white rounded-lg shadow-sm border-l-4 ${statusInfo.borderColor} p-4 sm:p-6 hover:shadow-md transition-shadow`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between space-y-3 sm:space-y-0">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center mb-3 space-y-2 sm:space-y-0 flex-wrap gap-2">
                          <div className="flex items-center">
                            <div className={`${statusInfo.color} mr-2`}>
                              {statusInfo.icon}
                            </div>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color}`}
                            >
                              {statusInfo.text}
                            </span>
                          </div>
                          <div
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              getPriceType(item).bgColor
                            } ${getPriceType(item).textColor} border ${
                              getPriceType(item).borderColor
                            }`}
                          >
                            {getPriceType(item).label}
                          </div>
                          <div className="sm:ml-4 flex items-center text-xs sm:text-sm text-gray-500">
                            <FiClock className="mr-1" />
                            {formatDate(item.created_at)}
                          </div>
                          {item.queue_type === "pending" && position > 0 && (
                            <div className="sm:ml-4 flex items-center text-xs sm:text-sm text-blue-600">
                              <FiUsers className="mr-1" />
                              Vị trí: #{position}
                            </div>
                          )}
                        </div>

                        <div className="mb-3">
                          <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-2 flex items-center">
                            <FiHome className="mr-2 text-gray-600" />
                            {propertySummary.title}
                            {item.queue_type === "processing" && (
                              <span className="ml-2 text-orange-600 text-sm">
                                (Đang xử lý)
                              </span>
                            )}
                          </h3>
                          {propertySummary.location && (
                            <p className="text-sm sm:text-base text-gray-600 flex items-start mb-2">
                              <FiMapPin className="mr-2 mt-1 flex-shrink-0 text-gray-400" />
                              <span className={styles.lineClamp2}>
                                {propertySummary.location}
                              </span>
                            </p>
                          )}
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center text-xs sm:text-sm text-gray-500 space-y-1 sm:space-y-0 sm:space-x-4">
                          <div className="flex items-center">
                            <FiArchive className="mr-1" />
                            Request ID: {item.request_id.slice(-8)}
                          </div>
                          {item.started_at && (
                            <div className="flex items-center">
                              <FiPlay className="mr-1" />
                              Bắt đầu: {formatDate(item.started_at)}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 sm:ml-4">
                        {item.queue_type === "pending" && position > 0 && (
                          <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-right">
                            <div className="text-xs text-gray-400">
                              Thời gian chờ ước tính
                            </div>
                            <div className="font-medium">
                              {position * 15} phút
                            </div>
                          </div>
                        )}
                        {item.queue_type === "processing" && (
                          <div className="flex items-center justify-center px-3 py-2 bg-orange-50 text-orange-600 rounded-lg text-xs sm:text-sm">
                            <FiLoader className="mr-1 animate-spin" />
                            Đang xử lý
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              // Render history item
              const fileCount = getFileCount(item);
              const propertySummary = getPropertySummary(item);

              return (
                <div
                  key={item.id}
                  className={`bg-white rounded-lg shadow-sm border-l-4 ${statusInfo.borderColor} p-4 sm:p-6 hover:shadow-md transition-shadow`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between space-y-3 sm:space-y-0">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center mb-3 space-y-2 sm:space-y-0 flex-wrap gap-2">
                        <div className="flex items-center">
                          <div className={`${statusInfo.color} mr-2`}>
                            {statusInfo.icon}
                          </div>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color}`}
                          >
                            {statusInfo.text}
                          </span>
                        </div>
                        <div
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            getPriceType(item).bgColor
                          } ${getPriceType(item).textColor} border ${
                            getPriceType(item).borderColor
                          }`}
                        >
                          {getPriceType(item).label}
                        </div>
                        <div className="sm:ml-4 flex items-center text-xs sm:text-sm text-gray-500">
                          <FiClock className="mr-1" />
                          {formatDate(item.created_at)}
                        </div>
                      </div>

                      <div className="mb-3">
                        <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-2 flex items-center">
                          <FiHome className="mr-2 text-gray-600" />
                          {propertySummary.title}
                        </h3>
                        {propertySummary.location && (
                          <p className="text-sm sm:text-base text-gray-600 flex items-start mb-2">
                            <FiMapPin className="mr-2 mt-1 flex-shrink-0 text-gray-400" />
                            <span className={styles.lineClamp2}>
                              {propertySummary.location}
                            </span>
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center text-xs sm:text-sm text-gray-500 space-y-1 sm:space-y-0 sm:space-x-4">
                        <div className="flex items-center">
                          <FiArchive className="mr-1" />
                          Task ID: {item.taskId.slice(-8)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 sm:ml-4">
                      <button
                        onClick={() => openDetailModal(item)}
                        className="flex items-center px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-xs sm:text-sm font-medium w-full sm:w-auto justify-center"
                      >
                        <FiEye className="mr-1" />
                        <span className="hidden sm:inline">Xem chi tiết</span>
                        <span className="sm:hidden">Chi tiết</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {showDetailModal && selectedHistory && (
          <div
            className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 ${styles.modalOverlay}`}
          >
            <div
              className={`bg-white rounded-lg max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden ${styles.scrollbarThin}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-800">
                    Chi Tiết Yêu Cầu Định Giá
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    {getStatusInfo(selectedHistory.status).text} •{" "}
                    {formatDate(selectedHistory.created_at)}
                  </p>
                </div>
                <button
                  onClick={closeDetailModal}
                  className="text-gray-400 hover:text-gray-600 text-xl sm:text-2xl p-1"
                >
                  ×
                </button>
              </div>

              {/* Content */}
              <div className="flex flex-col h-[calc(95vh-120px)] sm:h-[calc(90vh-120px)]">
                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
                  {/* Thông tin yêu cầu định giá */}
                  <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-base sm:text-lg font-semibold flex items-center">
                        <FiFileText className="mr-2 text-gray-600" />
                        Thông tin yêu cầu định giá
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          getStatusInfo(selectedHistory.status).bgColor
                        } ${getStatusInfo(selectedHistory.status).color}`}
                      >
                        {getStatusInfo(selectedHistory.status).text}
                      </span>
                    </div>
                    <div className="bg-white p-4 sm:p-6 rounded border text-sm text-gray-700">
                      {selectedHistory.form_data ? (
                        <PropertyFormDataDisplay
                          formData={selectedHistory.form_data}
                          isRental={
                            selectedHistory.current_task_title?.includes(
                              "thuê"
                            ) || false
                          }
                        />
                      ) : (
                        <div className="leading-relaxed whitespace-pre-wrap">
                          {selectedHistory.message || "Không có nội dung"}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Báo cáo phân tích - hiển thị trực tiếp */}
                  {selectedHistory.manus_response?.markdown && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold flex items-center">
                          <FiFileText className="mr-2 text-blue-600" />
                          Báo cáo phân tích chi tiết
                        </h3>
                        <button
                          onClick={openFullReportModal}
                          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          <FiEye className="mr-2" />
                          Xem báo cáo đầy đủ
                        </button>
                      </div>
                      <div className="bg-white rounded-lg border border-gray-200 p-6 max-h-96 overflow-y-auto">
                        <div className="prose prose-sm sm:prose-base max-w-none">
                          <Markdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              h1: ({ node, ...props }) => (
                                <h1
                                  className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2"
                                  {...props}
                                />
                              ),
                              h2: ({ node, ...props }) => (
                                <h2
                                  className="text-lg sm:text-xl font-bold text-gray-800 mb-3 mt-6"
                                  {...props}
                                />
                              ),
                              h3: ({ node, ...props }) => (
                                <h3
                                  className="text-base sm:text-lg font-bold text-gray-800 mb-2 mt-5"
                                  {...props}
                                />
                              ),
                              p: ({ node, ...props }) => (
                                <p
                                  className="text-gray-700 mb-3 leading-relaxed"
                                  {...props}
                                />
                              ),
                              ul: ({ node, ...props }) => (
                                <ul
                                  className="list-disc list-inside mb-4 space-y-1"
                                  {...props}
                                />
                              ),
                              ol: ({ node, ...props }) => (
                                <ol
                                  className="list-decimal list-inside mb-4 space-y-1"
                                  {...props}
                                />
                              ),
                              li: ({ node, ...props }) => (
                                <li className="text-gray-700" {...props} />
                              ),
                              blockquote: ({ node, ...props }) => (
                                <blockquote
                                  className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50 text-gray-700 italic mb-4"
                                  {...props}
                                />
                              ),
                              code: ({ node, inline, ...props }) =>
                                inline ? (
                                  <code
                                    className="bg-gray-100 text-red-600 px-1 py-0.5 rounded text-sm font-mono"
                                    {...props}
                                  />
                                ) : (
                                  <code
                                    className="block bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm font-mono mb-4"
                                    {...props}
                                  />
                                ),
                              pre: ({ node, ...props }) => (
                                <pre
                                  className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto mb-4"
                                  {...props}
                                />
                              ),
                              table: ({ node, ...props }) => (
                                <table
                                  className="w-full border-collapse border border-gray-300 mb-4"
                                  {...props}
                                />
                              ),
                              th: ({ node, ...props }) => (
                                <th
                                  className="border border-gray-300 bg-gray-100 px-4 py-2 text-left font-semibold"
                                  {...props}
                                />
                              ),
                              td: ({ node, ...props }) => (
                                <td
                                  className="border border-gray-300 px-4 py-2"
                                  {...props}
                                />
                              ),
                              a: ({ node, ...props }) => (
                                <a
                                  className="text-blue-600 hover:text-blue-800 underline"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  {...props}
                                />
                              ),
                              strong: ({ node, ...props }) => (
                                <strong
                                  className="font-bold text-gray-900"
                                  {...props}
                                />
                              ),
                              em: ({ node, ...props }) => (
                                <em
                                  className="italic text-gray-800"
                                  {...props}
                                />
                              ),
                            }}
                          >
                            {selectedHistory.manus_response.markdown}
                          </Markdown>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Kết quả xử lý - Files */}
                  {selectedHistory.crawl_response &&
                    (() => {
                      const allS3Files = getAllS3Files(selectedHistory);
                      return (
                        allS3Files.length > 0 && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center">
                              <FiDownload className="mr-2 text-gray-600" />
                              Files kết quả ({allS3Files.length} file)
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {allS3Files.map((file, index) => (
                                <div
                                  key={`${file.s3_url}-${index}`}
                                  className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow"
                                >
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm text-gray-800 mb-1 break-words">
                                        {file.filename}
                                      </div>
                                      <div className="text-xs text-gray-500 mb-2">
                                        {file.content_type} •{" "}
                                        {(file.file_size / 1024).toFixed(1)} KB
                                      </div>
                                      {file.subfolder && (
                                        <div className="text-xs text-gray-400 flex items-center mb-2">
                                          <span className="mr-1">📁</span>
                                          <span className="break-words">
                                            {file.subfolder}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <button
                                        onClick={() =>
                                          downloadFile(
                                            file.s3_url,
                                            file.filename
                                          )
                                        }
                                        className="flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg text-xs hover:bg-blue-600 transition-colors min-w-[80px]"
                                      >
                                        <FiDownload className="mr-1" />
                                        Tải
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      );
                    })()}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={closeDetailModal}
                    className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm sm:text-base font-medium"
                  >
                    Đóng
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Full Report Modal */}
        {showFullReportModal && selectedHistory && (
          <div
            className={`fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 ${styles.modalOverlay}`}
          >
            <div
              className={`bg-white rounded-lg max-w-7xl w-full h-[95vh] flex flex-col overflow-hidden ${styles.scrollbarThin} shadow-2xl`}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <div>
                  <h2 className="text-lg sm:text-2xl font-bold">
                    Báo cáo phân tích đầy đủ
                  </h2>
                  <p className="text-xs sm:text-sm opacity-90 mt-1">
                    {getStatusInfo(selectedHistory.status).text} •{" "}
                    {formatDate(selectedHistory.created_at)}
                  </p>
                </div>
                <button
                  onClick={closeFullReportModal}
                  className="text-white hover:text-gray-200 text-xl sm:text-2xl p-1 rounded hover:bg-white hover:bg-opacity-20 transition-colors"
                >
                  ×
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 sm:p-8">
                <div className="max-w-5xl mx-auto">
                  {/* Task Info */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-600">
                          Task ID:
                        </span>
                        <span className="ml-2 text-gray-800 font-mono">
                          {selectedHistory.taskId}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">
                          Trạng thái:
                        </span>
                        <span
                          className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                            getStatusInfo(selectedHistory.status).bgColor
                          } ${getStatusInfo(selectedHistory.status).color}`}
                        >
                          {getStatusInfo(selectedHistory.status).text}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">
                          Thời gian:
                        </span>
                        <span className="ml-2 text-gray-800">
                          {formatDate(selectedHistory.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Report Content */}
                  <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
                    <div className="prose prose-lg max-w-none">
                      <Markdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ node, ...props }) => (
                            <h1
                              className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 border-b border-gray-300 pb-3"
                              {...props}
                            />
                          ),
                          h2: ({ node, ...props }) => (
                            <h2
                              className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 mt-8 border-b border-gray-200 pb-2"
                              {...props}
                            />
                          ),
                          h3: ({ node, ...props }) => (
                            <h3
                              className="text-lg sm:text-xl font-bold text-gray-800 mb-3 mt-6"
                              {...props}
                            />
                          ),
                          h4: ({ node, ...props }) => (
                            <h4
                              className="text-base sm:text-lg font-semibold text-gray-800 mb-2 mt-5"
                              {...props}
                            />
                          ),
                          p: ({ node, ...props }) => (
                            <p
                              className="text-gray-700 mb-4 leading-relaxed text-base"
                              {...props}
                            />
                          ),
                          ul: ({ node, ...props }) => (
                            <ul
                              className="list-disc list-inside mb-6 space-y-2 ml-4"
                              {...props}
                            />
                          ),
                          ol: ({ node, ...props }) => (
                            <ol
                              className="list-decimal list-inside mb-6 space-y-2 ml-4"
                              {...props}
                            />
                          ),
                          li: ({ node, ...props }) => (
                            <li
                              className="text-gray-700 text-base"
                              {...props}
                            />
                          ),
                          blockquote: ({ node, ...props }) => (
                            <blockquote
                              className="border-l-4 border-blue-500 pl-6 py-3 bg-blue-50 text-gray-700 italic mb-6 text-base"
                              {...props}
                            />
                          ),
                          code: ({ node, inline, ...props }) =>
                            inline ? (
                              <code
                                className="bg-gray-100 text-red-600 px-2 py-1 rounded text-sm font-mono"
                                {...props}
                              />
                            ) : (
                              <code
                                className="block bg-gray-900 text-green-400 p-6 rounded-lg overflow-x-auto text-sm font-mono mb-6"
                                {...props}
                              />
                            ),
                          pre: ({ node, ...props }) => (
                            <pre
                              className="bg-gray-900 text-green-400 p-6 rounded-lg overflow-x-auto mb-6 text-sm font-mono"
                              {...props}
                            />
                          ),
                          table: ({ node, ...props }) => (
                            <table
                              className="w-full border-collapse border border-gray-300 mb-6 text-base"
                              {...props}
                            />
                          ),
                          th: ({ node, ...props }) => (
                            <th
                              className="border border-gray-300 bg-gray-100 px-4 py-3 text-left font-semibold"
                              {...props}
                            />
                          ),
                          td: ({ node, ...props }) => (
                            <td
                              className="border border-gray-300 px-4 py-3"
                              {...props}
                            />
                          ),
                          a: ({ node, ...props }) => (
                            <a
                              className="text-blue-600 hover:text-blue-800 underline font-medium"
                              target="_blank"
                              rel="noopener noreferrer"
                              {...props}
                            />
                          ),
                          strong: ({ node, ...props }) => (
                            <strong
                              className="font-bold text-gray-900"
                              {...props}
                            />
                          ),
                          em: ({ node, ...props }) => (
                            <em className="italic text-gray-800" {...props} />
                          ),
                        }}
                      >
                        {selectedHistory.manus_response.markdown}
                      </Markdown>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-between items-center p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    // Download markdown file
                    const markdownContent =
                      selectedHistory.manus_response?.markdown;
                    if (markdownContent) {
                      const blob = new Blob([markdownContent], {
                        type: "text/markdown",
                      });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = `bao_cao_dinh_gia_${
                        selectedHistory.taskId || Date.now()
                      }.md`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                    }
                  }}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base font-medium"
                >
                  <FiDownload className="mr-2" />
                  Tải xuống báo cáo (.md)
                </button>
                <button
                  onClick={closeFullReportModal}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm sm:text-base font-medium"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceHistoryScreen;
